import { defineStore } from 'pinia'
import api from '@/services/api'

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000
const ONE_DAY_MS     =      24 * 60 * 60 * 1000

function initState() {
  // A role-switch from another tab drops a short-lived handoff key in localStorage.
  // Only consume it when this page was opened specifically for that purpose (?via=switch),
  // so the main tab never accidentally picks it up on a regular refresh.
  const viaSwitch = new URLSearchParams(window.location.search).get('via') === 'switch'
  const raw = localStorage.getItem('_aff_handoff')
  if (raw && viaSwitch) {
    try {
      const h = JSON.parse(raw)
      if (Date.now() - h.ts < 15000) {
        sessionStorage.setItem('token', h.token)
        sessionStorage.setItem('user',  JSON.stringify(h.user))
        sessionStorage.setItem('org',   JSON.stringify(h.org))
      }
    } catch { /* malformed — ignore */ }
    localStorage.removeItem('_aff_handoff')
  } else if (raw && !viaSwitch) {
    // Stale handoff not meant for this tab — leave it; the target tab will consume it.
    // But if it's older than 15 s, clean it up to avoid accumulation.
    try {
      const h = JSON.parse(raw)
      if (Date.now() - h.ts >= 15000) localStorage.removeItem('_aff_handoff')
    } catch { localStorage.removeItem('_aff_handoff') }
  }

  // If this tab has a sessionStorage session (handoff or previous switch), use it.
  const ssToken = sessionStorage.getItem('token')
  if (ssToken) {
    return {
      token:        ssToken,
      user:         JSON.parse(sessionStorage.getItem('user') || 'null'),
      org:          JSON.parse(sessionStorage.getItem('org')  || 'null'),
      _sessionOnly: true,
    }
  }

  // Normal persistent session from localStorage
  const expiry = localStorage.getItem('token_expiry')
  if (expiry && Date.now() > Number(expiry)) {
    ['token', 'token_expiry', 'user', 'org'].forEach(k => localStorage.removeItem(k))
    return { token: null, user: null, org: null, _sessionOnly: false }
  }
  return {
    token:        localStorage.getItem('token') || null,
    user:         JSON.parse(localStorage.getItem('user') || 'null'),
    org:          JSON.parse(localStorage.getItem('org')  || 'null'),
    _sessionOnly: false,
  }
}

export const useAuthStore = defineStore('auth', {
  state: () => initState(),

  getters: {
    isLoggedIn:   (state) => !!state.token,
    role:         (state) => state.user?.role || null,
    orgId:        (state) => state.user?.org_id || null,
    fullName:     (state) => state.user ? `${state.user.first_name} ${state.user.last_name}` : '',
    isAffiliate:  (state) => !!state.user?.is_affiliate,
    dashboardRoute(state) {
      const map = {
        super_admin: '/super-admin/dashboard',
        org_admin:   '/admin/dashboard',
        staff:       '/staff/dashboard',
        attendee:    '/attendee/dashboard',
        affiliate:   '/affiliate/dashboard',
      }
      return map[state.user?.role] || '/'
    },
  },

  actions: {
    setAuth(token, user, org, rememberMe = false) {
      this.token = token
      this.user  = user
      this.org   = org
      if (this._sessionOnly) {
        sessionStorage.setItem('token', token)
        sessionStorage.setItem('user',  JSON.stringify(user))
        sessionStorage.setItem('org',   JSON.stringify(org))
      } else {
        const expiry = rememberMe ? THIRTY_DAYS_MS : ONE_DAY_MS
        localStorage.setItem('token',        token)
        localStorage.setItem('token_expiry', String(Date.now() + expiry))
        localStorage.setItem('user',         JSON.stringify(user))
        localStorage.setItem('org',          JSON.stringify(org))
      }
    },

    logout() {
      this.token = null
      this.user  = null
      this.org   = null
      if (this._sessionOnly) {
        sessionStorage.clear()
      } else {
        ['token', 'token_expiry', 'user', 'org'].forEach(k => localStorage.removeItem(k))
      }
    },

    async fetchMe() {
      const { data } = await api.get('/auth/me')
      this.user = data.data.user
      this.org  = data.data.org
      if (this._sessionOnly) {
        sessionStorage.setItem('user', JSON.stringify(this.user))
        sessionStorage.setItem('org',  JSON.stringify(this.org))
      } else {
        localStorage.setItem('user', JSON.stringify(this.user))
        localStorage.setItem('org',  JSON.stringify(this.org))
      }
    },

    // Opens the target role in a new tab without touching this tab's session.
    async switchRole(role, { newTab = true } = {}) {
      const { data } = await api.post('/auth/switch-role', { role })
      const p = data.data
      localStorage.setItem('_aff_handoff', JSON.stringify({
        token: p.token,
        user:  p.user,
        org:   p.org,
        ts:    Date.now(),
      }))
      if (!newTab) {
        window.location.assign('/affiliate/dashboard?via=switch')
        return
      }
      const tab = window.open('/affiliate/dashboard?via=switch', '_blank')
      if (!tab) {
        localStorage.removeItem('_aff_handoff')
        throw new Error('Popup blocked. Please allow pop-ups for this site and try again.')
      }
    },
  },
})
